import { format } from 'date-fns';
import { ColumnMapping, ExcelProcessorConfig, HeaderConfig, ParsedExcelData, ValidationError } from '@/lib/excel/types';
import { convertExcelDate, parseBoolean, parseNumber } from '@/lib/excel/date-utils';
import { roundNumber } from '@/lib/formatUtils';

const HEADERS = {
  // Main fields
  MA_DV: 'Mã đơn vị',
  SO_CT: 'Số c/từ*',
  NGAY_CT: 'Ngày chứng từ*',
  MA_NT: 'Ngoại tệ*',
  TY_GIA: 'Tỷ giá',
  LOAI_HD: 'Loại hóa đơn*',
  MA_KH: 'Mã nhà cung cấp*',
  TEN_KH: 'Tên nhà cung cấp',
  ONG_BA: 'Người giao hàng',
  TK: 'Tài khoản có*',
  SO_CT0: 'Số hóa đơn',
  NGAY_CT0: 'Ngày hóa đơn',
  SO_CT2: 'Ký hiệu',
  DIEN_GIAI: 'Diễn giải',

  // Detail fields
  MA_VT: 'Mã sản phẩm*',
  TEN_VT: 'Tên sản phẩm',
  DVT: 'Đvt*',
  MA_KHO: 'Mã kho*',
  MA_LO: 'Mã lô',
  MA_VI_TRI: 'Mã vị trí',
  SO_LUONG: 'Số lượng*',
  GIA_NT0: 'Giá ',
  TIEN_NT0: 'Tiền hàng ',
  TIEN_TCK_NT: 'Tiền hàng trước Ck ',
  TK_VT: 'Tk nợ*',
  MA_BP: 'Bộ phận',
  MA_PHI: 'Phí',
  MA_THUE: 'Mã thuế',

  // Thue fields
  SO_CT0_THUE: 'Số hóa đơn',
  SO_CT2_THUE: 'Ký hiệu',
  NGAY_CT0_THUE: 'Ngày hóa đơn',
  MA_MAU_CT: 'Mẫu hóa đơn',
  MA_MAU_BC: 'Mẫu báo cáo',
  MA_TC_THUE: 'Mã tính chất',
  MA_KH_THUE: 'Mã ncc',
  TEN_KH_THUE: 'Tên nhà cung cấp',
  DIA_CHI: 'Địa chỉ',
  MA_SO_THUE: 'Mã số thuế',
  TEN_VT_THUE: 'Tên hàng hóa - dịch vụ',
  TIEN_NT_THUE: 'Tiền hàng ',
  TK_THUE_NO: 'Tk thuế',
  TK_DU: 'Tk đối ứng',
  THUE_NT: 'Thuế ',
  MA_KH9: 'Cục thuế',
  MA_TT_THUE: 'Mã thanh toán',
  GHI_CHU: 'Ghi chú',
  MA_BP_THUE: 'Bộ phận',
  MA_VV_THUE: 'Vụ việc',
  MA_HD_THUE: 'Hợp đồng',
  MA_DTT_THUE: 'Đợt thanh toán',
  MA_KU_THUE: 'Khế ước',
  MA_PHI_THUE: 'Phí ',
  MA_SP: 'Sản phẩm',
  MA_LSX: 'Lệnh sản xuất'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  // Main
  { header: HEADERS.MA_DV, key: 'ma_dv', type: 'main' },
  { header: HEADERS.SO_CT, key: 'so_ct', type: 'main' },
  { header: HEADERS.NGAY_CT, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.MA_NT, key: 'ma_nt', type: 'main' },
  { header: HEADERS.TY_GIA, key: 'ty_gia', type: 'main' },
  { header: HEADERS.LOAI_HD, key: 'status', type: 'main' },
  { header: HEADERS.MA_KH, key: 'ma_kh', type: 'main' },
  { header: HEADERS.TEN_KH, key: 'ten_kh', type: 'main' },
  { header: HEADERS.ONG_BA, key: 'ong_ba', type: 'main' },
  { header: HEADERS.TK, key: 'tk', type: 'main' },
  { header: HEADERS.SO_CT0, key: 'so_ct0', type: 'main' },
  { header: HEADERS.NGAY_CT0, key: 'ngay_ct0', type: 'main' },
  { header: HEADERS.SO_CT2, key: 'so_ct2', type: 'main' },
  { header: HEADERS.DIEN_GIAI, key: 'dien_giai', type: 'main' },

  // Chi tiet
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'chi_tiet' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'chi_tiet' },
  { header: HEADERS.DVT, key: 'dvt', type: 'chi_tiet' },
  { header: HEADERS.MA_KHO, key: 'ma_kho', type: 'chi_tiet' },
  { header: HEADERS.MA_LO, key: 'ma_lo', type: 'chi_tiet' },
  { header: HEADERS.MA_VI_TRI, key: 'ma_vi_tri', type: 'chi_tiet' },
  { header: HEADERS.SO_LUONG, key: 'so_luong', type: 'chi_tiet' },
  { header: HEADERS.GIA_NT0, key: 'gia_nt0', type: 'chi_tiet' },
  { header: HEADERS.TIEN_NT0, key: 'tien_nt0', type: 'chi_tiet' },
  { header: HEADERS.TIEN_TCK_NT, key: 'tien_tck_nt', type: 'chi_tiet' },
  { header: HEADERS.TK_VT, key: 'tk_vt', type: 'chi_tiet' },
  { header: HEADERS.MA_BP, key: 'ma_bp', type: 'chi_tiet' },
  { header: HEADERS.MA_PHI, key: 'ma_phi', type: 'chi_tiet' },
  { header: HEADERS.MA_THUE, key: 'ma_thue', type: 'chi_tiet' },

  // Thue
  { header: HEADERS.SO_CT0_THUE, key: 'so_ct0', type: 'thue' },
  { header: HEADERS.SO_CT2_THUE, key: 'so_ct2', type: 'thue' },
  { header: HEADERS.NGAY_CT0_THUE, key: 'ngay_ct0', type: 'thue' },
  { header: HEADERS.MA_MAU_CT, key: 'ma_mau_ct', type: 'thue' },
  { header: HEADERS.MA_MAU_BC, key: 'ma_mau_bc', type: 'thue' },
  { header: HEADERS.MA_TC_THUE, key: 'ma_tc', type: 'thue' },
  { header: HEADERS.MA_KH_THUE, key: 'ma_kh', type: 'thue' },
  { header: HEADERS.TEN_KH_THUE, key: 'ten_kh', type: 'thue' },
  { header: HEADERS.DIA_CHI, key: 'dia_chi', type: 'thue' },
  { header: HEADERS.MA_SO_THUE, key: 'ma_so_thue', type: 'thue' },
  { header: HEADERS.TEN_VT_THUE, key: 'ten_vt', type: 'thue' },
  { header: HEADERS.TIEN_NT_THUE, key: 't_tien_nt', type: 'thue' },
  { header: HEADERS.TK_THUE_NO, key: 'tk_thue_no', type: 'thue' },
  { header: HEADERS.TK_DU, key: 'tk_du', type: 'thue' },
  { header: HEADERS.THUE_NT, key: 't_thue_nt', type: 'thue' },
  { header: HEADERS.MA_KH9, key: 'ma_kh9', type: 'thue' },
  { header: HEADERS.MA_TT_THUE, key: 'ma_tt', type: 'thue' },
  { header: HEADERS.GHI_CHU, key: 'ghi_chu', type: 'thue' },
  { header: HEADERS.MA_BP_THUE, key: 'ma_bp', type: 'thue' },
  { header: HEADERS.MA_VV_THUE, key: 'ma_vv', type: 'thue' },
  { header: HEADERS.MA_HD_THUE, key: 'ma_hd', type: 'thue' },
  { header: HEADERS.MA_DTT_THUE, key: 'ma_dtt', type: 'thue' },
  { header: HEADERS.MA_KU_THUE, key: 'ma_ku', type: 'thue' },
  { header: HEADERS.MA_PHI_THUE, key: 'ma_phi', type: 'thue' },
  { header: HEADERS.MA_SP, key: 'ma_sp', type: 'thue' },
  { header: HEADERS.MA_LSX, key: 'ma_lsx', type: 'thue' }
];

const getFieldValues = <T>(...candidates: Array<T[] | undefined>): T[] => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      const hasMeaningfulValue = candidate.some(
        value => value !== undefined && value !== null && String(value).trim() !== ''
      );
      if (hasMeaningfulValue) {
        return candidate;
      }
    }
  }
  return [];
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const hasCellValue = (value: unknown) => value !== undefined && value !== null && String(value).trim() !== '';

const resolveDetailTaxRate = (
  detailData: Record<string, MuaHangRecord[]>,
  thueData: Record<string, MuaHangRecord[]>,
  index: number
) => {
  if (hasCellValue(detailData.thue_suat?.[index])) {
    return roundNumber(parseNumber(detailData.thue_suat?.[index], 0, 6));
  }

  const taxBaseAmount = roundNumber(parseNumber(thueData.t_tien_nt?.[index], 0, 6));
  const taxAmount = roundNumber(parseNumber(thueData.t_thue_nt?.[index], 0, 6));

  if (taxBaseAmount > 0) {
    return roundNumber((taxAmount / taxBaseAmount) * 100);
  }

  return 0;
};

const resolveDetailTaxAmount = (
  detailData: Record<string, MuaHangRecord[]>,
  thueData: Record<string, MuaHangRecord[]>,
  amount: number,
  index: number
) => {
  if (hasCellValue(detailData.thue_nt?.[index])) {
    return roundNumber(parseNumber(detailData.thue_nt?.[index], 0, 6));
  }

  const taxRate = resolveDetailTaxRate(detailData, thueData, index);
  if (taxRate > 0) {
    return roundNumber((amount * taxRate) / 100);
  }

  if (hasCellValue(thueData.t_thue_nt?.[index])) {
    return roundNumber(parseNumber(thueData.t_thue_nt?.[index], 0, 6));
  }

  return 0;
};

const UUID_FIELDS = {
  main: ['ma_nt', 'ma_kh', 'ma_nvmh', 'tk', 'ma_gd'],
  chi_tiet: [
    'ma_vt',
    'dvt',
    'ma_kho',
    'ma_lo',
    'ma_vi_tri',
    'ma_thue',
    'tk_vt',
    'tk_cpxt',
    'ma_bp',
    'ma_vv',
    'ma_hd',
    'ma_dtt',
    'ma_ku',
    'ma_phi',
    'ma_cp0'
  ],
  chi_phi: ['ma_cp', 'ma_kh_cp', 'tk_cp'],
  thue: [
    'ma_thue',
    'ma_mau_ct',
    'ma_tc',
    'ma_kh',
    'tk_thue_no',
    'ma_kh9',
    'ma_tt',
    'ma_bp',
    'ma_vv',
    'ma_hd',
    'ma_dtt',
    'ma_ku',
    'ma_phi',
    'ma_sp',
    'ma_sp',
    'ma_lsx',
    'tk_du'
  ]
};

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: Record<string, MuaHangRecord[]>, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section?.[f] || []]));

  const main = data.main || {};
  const chi_tiet = data.chi_tiet || {};
  const chi_phi = data.chi_phi || {};
  const thue = data.thue || {};
  const thueMaThueValues = getFieldValues(thue.ma_thue, chi_tiet.ma_thue);

  return {
    main: {
      ...build(main, UUID_FIELDS.main),
      ma_gd: (main.ma_gd || []).map((val: MuaHangRecord) => (val ? `HD4${val}` : val))
    },
    chi_tiet: build(chi_tiet, UUID_FIELDS.chi_tiet),
    chi_phi: build(chi_phi, UUID_FIELDS.chi_phi),
    thue: {
      ...build(thue, UUID_FIELDS.thue),
      ma_thue: thueMaThueValues
    }
  };
};

const ValidateUUID = (response: MuaHangRecord, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main', 'chi_tiet', 'chi_phi', 'thue'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const processThueRows = (
  mainData: Record<string, MuaHangRecord[]>,
  detailData: Record<string, MuaHangRecord[]>,
  thueData: Record<string, MuaHangRecord[]>,
  UUIDResponse: MuaHangRecord,
  rowCount: number
) => {
  const thueMap = new Map<string, ImportBody['thue_hoa_don']>();
  const maThueValues = getFieldValues(thueData.ma_thue, detailData.ma_thue);
  const maThueUuidValues = getFieldValues(UUIDResponse.thue?.ma_thue, UUIDResponse.chi_tiet?.ma_thue);

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = mainData.so_ct?.[i] || '';
    const recordKey = `${documentNumber}`;
    const maThue = maThueValues[i];
    if (maThue && String(maThue).trim() !== '') {
      const key = maThueUuidValues[i] || maThue;
      const taxBaseAmount = roundNumber(parseNumber(thueData.t_tien_nt?.[i], 0, 6));
      const taxAmount = roundNumber(parseNumber(thueData.t_thue_nt?.[i], 0, 6));
      const taxRate = taxBaseAmount > 0 ? roundNumber((taxAmount / taxBaseAmount) * 100) : 0;
      const rowsForRecord = thueMap.get(recordKey) || [];
      const existing = rowsForRecord.find(row => row.ma_thue === key);

      if (!existing) {
        const ngayCt0 = convertExcelDate(thueData.ngay_ct0?.[i]) || format(new Date(), 'yyyy-MM-dd');
        rowsForRecord.push({
          line: thueMap.size + 1,
          so_ct0: thueData.so_ct0?.[i] || undefined,
          so_ct2: thueData.so_ct2?.[i] || null,
          ngay_ct0: ngayCt0,
          ma_thue: key,
          thue_suat: taxRate,
          ma_mau_ct: UUIDResponse.thue?.ma_mau_ct?.[i] || null,
          ma_mau_bc: thueData.ma_mau_bc?.[i] || '3',
          ma_tc_thue: UUIDResponse.thue?.ma_tc?.[i] || undefined,
          ma_kh: UUIDResponse.thue?.ma_kh?.[i] || undefined,
          ten_kh_thue: thueData.ten_kh?.[i] || undefined,
          dia_chi: thueData.dia_chi?.[i] || undefined,
          ma_so_thue: thueData.ma_so_thue?.[i] || undefined,
          ten_vt_thue: thueData.ten_vt?.[i] || undefined,
          t_tien_nt: taxBaseAmount,
          t_tien: taxBaseAmount,
          tk_thue_no: UUIDResponse.thue?.tk_thue_no?.[i] || undefined,
          tk_du: UUIDResponse.thue?.tk_du?.[i] || undefined,
          ten_tk_thue_no: undefined,
          t_thue_nt: taxAmount,
          t_thue: taxAmount,
          ma_kh9: UUIDResponse.thue?.ma_kh9?.[i] || null,
          ma_tt: UUIDResponse.thue?.ma_tt?.[i] || null,
          ghi_chu: thueData.ghi_chu?.[i] || undefined,
          ma_bp: UUIDResponse.thue?.ma_bp?.[i] || null,
          ma_vv: UUIDResponse.thue?.ma_vv?.[i] || null,
          ma_hd: UUIDResponse.thue?.ma_hd?.[i] || null,
          ma_dtt: UUIDResponse.thue?.ma_dtt?.[i] || null,
          ma_ku: UUIDResponse.thue?.ma_ku?.[i] || null,
          ma_phi: UUIDResponse.thue?.ma_phi?.[i] || null,
          ma_cp0: UUIDResponse.thue?.ma_cp0?.[i] || null,
          ma_sp: UUIDResponse.thue?.ma_sp?.[i] || null,
          ma_lsx: UUIDResponse.thue?.ma_lsx?.[i] || null,
          so_ct_dh: thueData.so_ct_dh?.[i] || undefined
        });
        thueMap.set(recordKey, rowsForRecord);
      } else {
        existing.t_tien_nt = roundNumber(existing.t_tien_nt + parseNumber(thueData.t_tien_nt?.[i], 0, 6));
        existing.t_tien = existing.t_tien_nt;
        existing.t_thue_nt = roundNumber(existing.t_thue_nt + parseNumber(thueData.t_thue_nt?.[i], 0, 6));
        existing.t_thue = existing.t_thue_nt;
      }
    }
  }

  thueMap.forEach(rows => {
    if (!rows) return;
    rows.forEach((row, index) => {
      row.line = index + 1;
    });
  });

  return thueMap;
};

const processChiPhiRows = (
  chiPhiData: Record<string, MuaHangRecord[]>,
  UUIDResponse: MuaHangRecord,
  rowCount: number
): ImportBody['chi_phi_hoa_don'] => {
  const chiPhiMap = new Map<string, MuaHangRecord>();

  for (let i = 0; i < rowCount; i++) {
    const maCp = chiPhiData.ma_cp?.[i];
    if (maCp && String(maCp).trim() !== '') {
      const maCpUuid = UUIDResponse.chi_phi?.ma_cp?.[i] || null;
      const key = maCpUuid || maCp;
      if (!chiPhiMap.has(key)) {
        chiPhiMap.set(key, {
          line: chiPhiMap.size + 1,
          ma_cp: maCpUuid,
          ten_cp: chiPhiData.ten_cp?.[i] || undefined,
          tien_cp_nt: roundNumber(parseNumber(chiPhiData.tien_cp_nt?.[i], 0, 6)),
          tien_cp: roundNumber(parseNumber(chiPhiData.tien_cp_nt?.[i], 0, 6)),
          ma_kh: UUIDResponse.chi_phi?.ma_kh_cp?.[i] || null,
          ten_kh: chiPhiData.ten_kh_cp?.[i] || undefined,
          tk: UUIDResponse.chi_phi?.tk_cp?.[i] || undefined
        });
      } else {
        const existing = chiPhiMap.get(key)!;
        existing.tien_cp_nt = roundNumber(existing.tien_cp_nt + parseNumber(chiPhiData.tien_cp_nt?.[i], 0, 6));
        existing.tien_cp = existing.tien_cp_nt;
      }
    }
  }

  return Array.from(chiPhiMap.values());
};

const processChiTietRows = (
  groupedRecords: Map<string, ImportBody>,
  mainData: Record<string, MuaHangRecord[]>,
  detailData: Record<string, MuaHangRecord[]>,
  thueData: Record<string, MuaHangRecord[]>,
  UUIDResponse: MuaHangRecord,
  rowCount: number
): Map<string, ImportBody['chi_tiet_hoa_don']> => {
  const chiTietMap = new Map<string, ImportBody['chi_tiet_hoa_don']>();

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = mainData.so_ct?.[i] || '';
    const recordKey = `${documentNumber}`;

    const record = groupedRecords.get(recordKey);
    if (!record) continue;

    if ((!record.dien_giai || record.dien_giai.trim() === '') && mainData.dien_giai?.[i]?.trim()) {
      record.dien_giai = String(mainData.dien_giai[i]).trim();
    }

    const quantity = roundNumber(parseNumber(detailData.so_luong?.[i], 0, 6));
    const price0 = roundNumber(parseNumber(detailData.gia_nt0?.[i], 0, 6));
    const amount0 = roundNumber(parseNumber(detailData.tien_nt0?.[i], 0, 6) || quantity * price0);
    const cost = roundNumber(parseNumber(detailData.cp_nt?.[i], 0, 6));
    const directDelivery = parseBoolean(detailData.px_dd?.[i]);

    const amountBeforeDiscount = roundNumber(parseNumber(detailData.tien_tck_nt?.[i], 0, 6) || amount0);
    const discountRate = roundNumber(parseNumber(detailData.tl_ck?.[i], 0, 6));
    const discount = roundNumber(parseNumber(detailData.ck_nt?.[i], 0, 6));

    const detailsForKey = chiTietMap.get(recordKey) || [];

    const detailItem = {
      line: detailsForKey.length + 1,
      ma_vt: UUIDResponse.chi_tiet?.ma_vt?.[i] || '',
      dvt: UUIDResponse.chi_tiet?.dvt?.[i] || '',
      ma_kho: UUIDResponse.chi_tiet?.ma_kho?.[i] || '',
      ma_lo: detailData.ma_lo?.[i] || undefined,
      ma_vi_tri: UUIDResponse.chi_tiet?.ma_vi_tri?.[i] || detailData.ma_vi_tri?.[i] || undefined,
      so_luong: quantity,
      sl_cl: quantity,
      gia_nt0: price0,
      tien_nt0: amount0,
      tien_tck_nt: amountBeforeDiscount,
      tien_tck: amountBeforeDiscount,
      tl_ck: discountRate,
      ck_nt: discount,
      ma_thue: UUIDResponse.chi_tiet?.ma_thue?.[i] || undefined,
      ma_bp: UUIDResponse.chi_tiet?.ma_bp?.[i] || undefined,
      ma_vv: UUIDResponse.chi_tiet?.ma_vv?.[i] || undefined,
      ma_hd: UUIDResponse.chi_tiet?.ma_hd?.[i] || undefined,
      ma_dtt: UUIDResponse.chi_tiet?.ma_dtt?.[i] || undefined,
      ma_ku: UUIDResponse.chi_tiet?.ma_ku?.[i] || undefined,
      ma_phi: UUIDResponse.chi_tiet?.ma_phi?.[i] || undefined,
      ma_cp0: UUIDResponse.chi_tiet?.ma_cp0?.[i] || undefined,
      tk_vt: UUIDResponse.chi_tiet?.tk_vt?.[i] || undefined,
      tk_cpxt: UUIDResponse.chi_tiet?.tk_cpxt?.[i] || undefined,
      px_dd: directDelivery || false,
      cp_nt: cost || 0,
      gia_ton: 0,
      gia_ton_nt: 0,
      thue_nt: resolveDetailTaxAmount(detailData, thueData, amount0, i)
    };

    detailsForKey.push(detailItem);
    chiTietMap.set(recordKey, detailsForKey);
  }

  return chiTietMap;
};

const processMainRows = (
  mainData: Record<string, MuaHangRecord[]>,
  UUIDResponse: MuaHangRecord,
  entityUnitUUID: string,
  rowCount: number,
  defaultMaGD: string
): Map<string, ImportBody> => {
  const groupedRecords = new Map<string, ImportBody>();

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = mainData.so_ct?.[i] || '';
    const recordKey = `${documentNumber}`;

    if (!groupedRecords.has(recordKey)) {
      const isDirectExport = parseBoolean(mainData.xt_yn?.[i]);
      const isTransfer = parseBoolean(mainData.transfer_yn?.[i]);

      groupedRecords.set(recordKey, {
        hdmh_yn: true,
        pn_yn: true,
        pc_tao_yn: false,
        xt_yn: isDirectExport,
        ck_yn: false,
        loai_ck: '4',
        ck_tl_nt: 0,
        so_ct: documentNumber || undefined,
        ngay_ct: convertExcelDate(mainData.ngay_ct?.[i]),
        ngay_lct: convertExcelDate(mainData.ngay_lct?.[i]) || convertExcelDate(mainData.ngay_ct?.[i]),
        ma_nt: UUIDResponse.main?.ma_nt?.[i] || mainData.ma_nt?.[i] || '',
        ty_gia: mainData.ty_gia?.[i] || '1',
        so_ct0: mainData.so_ct0?.[i] || undefined,
        ngay_ct_hddt: convertExcelDate(mainData.ngay_ct_hddt?.[i]) || undefined,
        so_ct2: mainData.so_ct2?.[i] || undefined,
        so_pn: mainData.so_pn?.[i] || undefined,
        ngay_pn: convertExcelDate(mainData.ngay_pn?.[i]),
        ma_kh: UUIDResponse.main?.ma_kh?.[i] || '',
        ong_ba: mainData.ong_ba?.[i] || undefined,
        dien_giai: (mainData.dien_giai?.[i]?.trim() || '') as string,
        ma_nvmh: UUIDResponse.main?.ma_nvmh?.[i] || '',
        tk: UUIDResponse.main?.tk?.[i] || '',
        ma_tt: UUIDResponse.main?.ma_tt?.[i] || UUIDResponse.thue?.ma_tt?.[i] || mainData.ma_tt?.[i] || '',
        transfer_yn: isTransfer,
        unit_id: entityUnitUUID,
        ma_ngv: '1',
        ma_gd: UUIDResponse.main?.ma_gd?.[i] || mainData.ma_gd?.[i] || defaultMaGD,
        status: mainData.status?.[i] || '5',
        chi_tiet_hoa_don: []
      });
    }
  }

  return groupedRecords;
};

const calculateTotals = (chiTiet: ImportBody['chi_tiet_hoa_don']) => {
  let t_so_luong = 0;
  let t_tien_nt = 0;
  let t_thue_nt = 0;

  chiTiet.forEach(item => {
    t_so_luong += item.so_luong || 0;
    t_tien_nt += item.tien_nt0 || 0;
    t_thue_nt += item.thue_nt || 0;
  });

  const t_tt_nt = t_tien_nt + t_thue_nt;

  return {
    t_so_luong: roundNumber(t_so_luong),
    t_tien_nt: roundNumber(t_tien_nt),
    t_thue_nt: roundNumber(t_thue_nt),
    t_tt_nt: roundNumber(t_tt_nt),
    t_tt: roundNumber(t_tt_nt)
  };
};

const TransformToBodies = (
  dataRows: MuaHangRecord[][],
  parsedData: ParsedExcelData,
  UUIDResponse: MuaHangRecord,
  context: { entityUnitUUID: string; defaultMaGD?: string }
): ImportBody[] => {
  const main = parsedData['main'] || {};
  const chi_tiet = parsedData['chi_tiet'] || {};
  const chi_phi = parsedData['chi_phi'] || {};
  const thue = parsedData['thue'] || {};

  const rowCount = dataRows.length;
  const defaultMaGD = context.defaultMaGD || 'HD';

  const groupedRecords = processMainRows(main, UUIDResponse, context.entityUnitUUID, rowCount, defaultMaGD);
  const chiTietMap = processChiTietRows(groupedRecords, main, chi_tiet, thue, UUIDResponse, rowCount);
  const chiPhiRows = processChiPhiRows(chi_phi, UUIDResponse, rowCount);
  const thueMap = processThueRows(main, chi_tiet, thue, UUIDResponse, rowCount);

  const records = Array.from(groupedRecords.values());
  records.forEach(record => {
    const key = `${record.so_ct || ''}`;
    record.chi_tiet_hoa_don = chiTietMap.get(key) || [];

    const totals = calculateTotals(record.chi_tiet_hoa_don);
    record.t_so_luong = totals.t_so_luong;
    record.t_tien_nt = totals.t_tien_nt;
    record.t_thue_nt = totals.t_thue_nt;
    record.t_thue = totals.t_thue_nt;
    record.t_tt_nt = totals.t_tt_nt;
    record.t_tt = totals.t_tt;

    if (chiPhiRows && chiPhiRows.length > 0) {
      record.chi_phi_hoa_don = chiPhiRows;
    }
    const thueRows = thueMap.get(key) || [];
    if (thueRows.length > 0) {
      record.thue_hoa_don = thueRows;
    }
  });

  return records;
};

export interface ImportBody {
  hdmh_yn?: boolean;
  pn_yn?: boolean;
  pc_tao_yn?: boolean;
  xt_yn?: boolean;
  ck_yn?: boolean;
  loai_ck?: string;
  ck_tl_nt?: number;
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  ma_nt?: string;
  ty_gia?: string;
  so_ct0?: string;
  ngay_ct_hddt?: string;
  so_ct2?: string;
  so_pn?: string;
  ngay_pn?: string;
  ma_kh: string;
  ong_ba?: string;
  dien_giai?: string;
  ma_nvmh?: string;
  tk?: string;
  ma_tt?: string;
  transfer_yn?: boolean;
  unit_id: string;
  ma_ngv: string;
  ma_gd?: string;
  status?: string;
  t_so_luong?: number;
  t_tien_nt?: number;
  t_tien0?: number;
  t_thue_nt?: number;
  t_thue?: number;
  t_tt_nt?: number;
  t_tt?: number;
  chi_tiet_hoa_don: Array<{
    line: number;
    ma_vt: string;
    dvt: string;
    ma_kho: string;
    ma_lo?: string;
    ma_vi_tri?: string;
    so_luong: number;
    gia_nt0: number;
    tien_nt0: number;
    tien_tck_nt?: number;
    tien_tck?: number;
    tl_ck?: number;
    ck_nt?: number;
    ma_thue?: string;
    thue_nt?: number;
    ma_bp?: string;
    ma_vv?: string;
    ma_hd?: string;
    ma_dtt?: string;
    ma_ku?: string;
    ma_phi?: string;
    ma_cp0?: string;
    tk_vt?: string;
    tk_cpxt?: string;
    px_dd?: boolean;
    cp_nt?: number;
    gia_nt?: number;
    sl_cl?: number;
    gia_ton?: number;
  }>;
  chi_phi_hoa_don?: Array<{
    line: number;
    ma_cp: string | null;
    ten_cp?: string;
    tien_cp_nt: number;
    tien_cp?: number;
    ma_kh?: string | null;
    ten_kh?: string;
    tk?: string;
  }>;
  thue_hoa_don?: Array<{
    line: number;
    so_ct0?: string;
    so_ct2?: string | null;
    ngay_ct0?: string;
    ma_thue: string;
    thue_suat?: number;
    ma_mau_ct?: string | null;
    ma_mau_bc?: string;
    ma_tc_thue?: string;
    ma_kh?: string;
    ten_kh_thue?: string;
    dia_chi?: string;
    ma_so_thue?: string;
    ten_vt_thue?: string;
    t_tien_nt: number;
    t_tien?: number;
    tk_thue_no?: string;
    tk_du?: string;
    ten_tk_thue_no?: string;
    t_thue_nt: number;
    t_thue?: number;
    ma_kh9?: string | null;
    ma_tt?: string | null;
    ghi_chu?: string;
    ma_bp?: string | null;
    ma_vv?: string | null;
    ma_hd?: string | null;
    ma_dtt?: string | null;
    ma_ku?: string | null;
    ma_phi?: string | null;
    ma_cp0?: string | null;
    ma_sp?: string | null;
    ma_lsx?: string | null;
    so_ct_dh?: string;
  }>;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
